import { useEffect, useMemo, useState } from "react";
import { PokemonType } from "types/battle-engine/battle";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Button } from "@/components/ui/buttons/button";
import { Alert } from "@/components/alert/alert";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { EnergyCard } from "@/components/cards/energy-card";
import { ItemCard } from "@/components/cards/item-card";
import { PokemonCard } from "@/components/cards/pokemon-card";
import { getItemCardDefinition } from "utils/item-cards/get-item-card-definition";
import { getItemCardIconUrl } from "@/components/cards/utils/get-item-card-icon-url";
import { useOpenPack } from "@/hooks/queries/use-packs";
import { OpenPackOutput } from "types/web-app";
import { PackDefinition } from "types/packs/pack-definition";
import { PackCardVisual } from "./pack-card-visual";
import { formatPokeName } from "./pack-ui";
import { preloadPokemonSprite } from "./preload-pokemon-sprite";

interface PackRevealOverlayProps {
  packInstanceId: number;
  definition: PackDefinition;
  onClose: () => void;
}

type EnergyGrant = OpenPackOutput["energy"][number];

/** One card in the reveal sequence (energy → items → pokemon climax). */
type RevealItem =
  | { kind: "energy"; type: EnergyGrant["type"] }
  | { kind: "item"; itemCardKey: string }
  | { kind: "pokemon"; pokemon: NonNullable<OpenPackOutput["pokemon"]> };

/** Repeat `item` `count` times so duplicates reveal one-by-one (no "x2"). */
const repeat = <T,>(count: number, make: () => T): T[] =>
  Array.from({ length: Math.max(1, count) }, make);

/** Flatten the pack result into the reveal order the sequence walks through. */
const buildRevealQueue = (result: OpenPackOutput): RevealItem[] => [
  ...result.energy.flatMap((g) =>
    repeat(g.amount, () => ({ kind: "energy" as const, type: g.type })),
  ),
  ...result.item_cards.flatMap((g) =>
    repeat(g.amount, () => ({ kind: "item" as const, itemCardKey: g.item_card_key })),
  ),
  ...(result.pokemon ? [{ kind: "pokemon" as const, pokemon: result.pokemon }] : []),
];

/**
 * Staged pack-open reveal (core-packs C2 "dopamine moment"): tap the pack, then
 * reveal each grant one card at a time using the real card components — energy
 * first, then item cards, saving the pokemon (the climax) for last with extra
 * suspense and VFX.
 */
export const PackRevealOverlay = ({
  packInstanceId,
  definition,
  onClose,
}: PackRevealOverlayProps) => {
  const [result, setResult] = useState<OpenPackOutput | null>(null);
  const [index, setIndex] = useState(0);
  // Sprite preloaded into browser cache so the pokemon card shows filled-in.
  const [spriteReady, setSpriteReady] = useState(false);
  // Anticipation phase shown right before the pokemon (the climax) pops in.
  const [suspense, setSuspense] = useState(false);
  const openPack = useOpenPack();

  const queue = useMemo(() => (result ? buildRevealQueue(result) : []), [result]);

  const handleOpen = async () => {
    if (openPack.isPending) return;
    try {
      const response = await openPack.mutateAsync({ pack_instance_id: packInstanceId });
      if (!response.ok || !response.data) {
        Alert(response.message || "Failed to open pack");
        onClose();
        return;
      }
      setResult(response.data);
      const mon = response.data.pokemon;
      if (mon) {
        preloadPokemonSprite(mon.base_pokemon_name, mon.is_shiny).then(() =>
          setSpriteReady(true),
        );
      }
    } catch (error) {
      console.error("Failed to open pack:", error);
      Alert("Failed to open pack");
      onClose();
    }
  };

  const current = queue[index];
  const isLast = index >= queue.length - 1;
  const nextIsPokemon = queue[index + 1]?.kind === "pokemon";

  // Once the suspense phase is up and the sprite is ready, reveal the pokemon.
  useEffect(() => {
    if (suspense && spriteReady) {
      const t = setTimeout(() => {
        setSuspense(false);
        setIndex((i) => i + 1);
      }, 900);
      return () => clearTimeout(t);
    }
  }, [suspense, spriteReady]);

  const advance = () => {
    if (isLast) {
      onClose();
    } else if (nextIsPokemon) {
      setSuspense(true); // build tension before the climax
    } else {
      setIndex((i) => i + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center overflow-hidden">
      <style>{`
        @keyframes pack-shake {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(-4deg) scale(1.03); }
          75% { transform: rotate(4deg) scale(1.03); }
        }
        @keyframes card-pop {
          0% { transform: scale(0.4) translateY(30px); opacity: 0; }
          60% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes suspense-pulse {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.35); opacity: 1; }
        }
        @keyframes suspense-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.08)_0%,rgba(0,0,0,1)_70%)]" />

      {!result && (
        <div className="relative z-10 flex flex-col items-center gap-8 px-6">
          <div
            className="cursor-pointer active:scale-95 transition-transform"
            style={openPack.isPending ? { animation: "pack-shake 0.3s infinite" } : undefined}
            onClick={handleOpen}
          >
            <PackCardVisual definition={definition} />
          </div>

          <StrokedText
            fontSize={20}
            strokeWidth={5}
            className={openPack.isPending ? "opacity-50" : "animate-bounce"}
          >
            {openPack.isPending ? "OPENING..." : "TAP TO OPEN"}
          </StrokedText>
        </div>
      )}

      {suspense && (
        <div className="relative z-10 flex flex-col items-center gap-8 px-6">
          <div className="relative flex items-center justify-center w-40 h-40">
            <div
              className="absolute inset-0 rounded-full border-4 border-pk-yellow-2/70 border-t-transparent"
              style={{ animation: "suspense-spin 0.9s linear infinite" }}
            />
            <div
              className="w-16 h-16 rounded-full bg-[radial-gradient(circle,rgba(255,215,0,0.9)_0%,rgba(255,140,0,0.5)_60%,transparent_75%)]"
              style={{ animation: "suspense-pulse 0.7s ease-in-out infinite" }}
            />
          </div>
          <StrokedText fontSize={18} strokeWidth={5} className="uppercase text-pk-yellow-2">
            {spriteReady ? "A partner appears..." : "Summoning..."}
          </StrokedText>
        </div>
      )}

      {current && !suspense && (
        <div
          className="relative z-10 flex flex-col items-center gap-6 px-6 w-full h-full justify-center cursor-pointer"
          onClick={advance}
        >
          <StrokedText fontSize={18} strokeWidth={5} className="uppercase text-pk-gray-5">
            {current.kind === "pokemon"
              ? "Your new partner"
              : `Card ${index + 1} of ${queue.length}`}
          </StrokedText>

          <RevealCard key={index} item={current} />

          {isLast ? (
            <Button variant="yellow" className="mt-2" onClick={onClose}>
              DONE
            </Button>
          ) : (
            <span className="text-pk-yellow-2 animate-bounce text-sm font-bold tracking-widest uppercase mt-2">
              ▼ Tap to continue
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/** Renders the current reveal item as its real card component, wrapped in a
 *  pop-in entrance and layered reveal VFX (pokemon + shiny get extra flair). */
const RevealCard = ({ item }: { item: RevealItem }) => {
  const isPokemon = item.kind === "pokemon";
  const isShiny = isPokemon && item.pokemon.is_shiny;

  return (
    <div className="relative flex flex-col items-center">
      <div
        className="relative flex items-center justify-center"
        style={{ animation: "card-pop 0.55s cubic-bezier(0.34,1.56,0.64,1) both" }}
      >
        {/* burst behind the card as it pops in */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-[220px] h-[300px]">
            <AnimatedVFX
              id="pack-card-burst"
              spriteUrl={
                isShiny
                  ? "/assets/spells/tile-fx/magic-puff-yellow.png"
                  : "/assets/spells/tile-fx/magic-puff-white.png"
              }
              position={{ x: 110, y: 150 }}
              spriteScale={5}
              className="pointer-events-none"
            />
            {isPokemon && (
              <AnimatedVFX
                id="pack-card-stars"
                spriteUrl="/assets/spells/vfx/stars_effect_000__40.png"
                position={{ x: 110, y: 150 }}
                spriteScale={isShiny ? 4 : 2.5}
                loops={isShiny ? 4 : 2}
                className="pointer-events-none opacity-80"
              />
            )}
          </div>
        </div>

        {item.kind === "energy" && <EnergyCard type={item.type as unknown as PokemonType} />}

        {item.kind === "item" && <ItemCardReveal itemCardKey={item.itemCardKey} />}

        {item.kind === "pokemon" && (
          <PokemonCard
            pokeName={item.pokemon.base_pokemon_name}
            isShiny={item.pokemon.is_shiny}
            isFullArt={item.pokemon.is_full_art}
            foil={isShiny ? { texture: "rainbow", borderMetal: "gold" } : true}
            autoShine
          />
        )}
      </div>

      {isPokemon && (
        <div className="flex flex-col items-center gap-1 mt-3">
          <StrokedText
            fontSize={24}
            strokeWidth={5}
            className={
              isShiny ? "text-pk-yellow-2 drop-shadow-[0_0_12px_rgba(255,215,0,0.7)]" : ""
            }
          >
            {formatPokeName(item.pokemon.base_pokemon_name)}
          </StrokedText>
          {isShiny && (
            <StrokedText fontSize={14} strokeWidth={4} className="text-pk-yellow-2">
              ✨ SHINY ✨
            </StrokedText>
          )}
          <StrokedText fontSize={12} strokeWidth={3} className="text-pk-gray-5">
            {`Potential ${item.pokemon.purity}/9`}
          </StrokedText>
        </div>
      )}
    </div>
  );
};

/** Resolves the item-card definition + icon and renders the real ItemCard. */
const ItemCardReveal = ({ itemCardKey }: { itemCardKey: string }) => {
  const def = getItemCardDefinition(itemCardKey);
  if (!def) {
    return (
      <div className="w-[170px] aspect-[5/7] flex items-center justify-center rounded-xl border border-white/20 bg-pk-night-1/60">
        <StrokedText fontSize={14} strokeWidth={4}>
          {itemCardKey}
        </StrokedText>
      </div>
    );
  }
  return <ItemCard def={def} iconUrl={getItemCardIconUrl(def)} />;
};
